import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams, Link } from 'react-router-dom';
import { formatStamp } from '../lib/timeFields.jsx';
import { useVenueTz } from '@/lib/venueTz.js';

export default function Activity() {
  const { tenantSlug } = useParams();
  const tz = useVenueTz();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/tenants/${tenantSlug}/activity?limit=${limit}`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setData(d); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tenantSlug, limit]);

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (error) return <div className="text-destructive">Error: {error}</div>;
  if (!data?.actions?.length) return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Activity</h1>
      <div className="text-muted-foreground">No activity recorded yet.</div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Activity</h1>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">{data.count} actions</span>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="px-2 py-1 border border-input rounded-md bg-background"
          >
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
            <option value={250}>Last 250</option>
            <option value={500}>Last 500</option>
          </select>
        </div>
      </div>

      {/* Mobile: stacked cards (the table below is md+ only) */}
      <div className="md:hidden space-y-2">
        {data.actions.map((a) => (
          <div key={a.id} className="border border-border rounded-md p-3 bg-card text-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {a.event_id ? (
                  <Link to={`/${tenantSlug}/event/${a.event_id}`} className="hover:underline">
                    <span className="font-medium">{a.talent || '—'}</span>
                    {a.date && <span className="text-muted-foreground ml-2">{formatDate(a.date)}</span>}
                  </Link>
                ) : <span className="font-medium text-muted-foreground">—</span>}
              </div>
              {a.succeeded ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 flex-shrink-0">✓ ok</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-destructive flex-shrink-0" title={a.error_message || ''}>✗ failed</span>
              )}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {a.platform} · {a.action}
            </div>
            <div className="mt-1 text-xs text-muted-foreground font-mono">
              {formatWhen(a.performed_at, tz)} · {a.performed_by ? a.performed_by.slice(0, 8) : 'system'}
            </div>
          </div>
        ))}
      </div>

      <div className="border border-border rounded-md overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Event</th>
              <th className="px-3 py-2 font-medium">Platform</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Result</th>
              <th className="px-3 py-2 font-medium">By</th>
            </tr>
          </thead>
          <tbody>
            {data.actions.map((a) => (
              <tr key={a.id} className="border-t border-border hover:bg-accent/30">
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {formatWhen(a.performed_at, tz)}
                </td>
                <td className="px-3 py-2">
                  {a.event_id ? (
                    <Link to={`/${tenantSlug}/event/${a.event_id}`} className="hover:underline">
                      <span className="font-medium">{a.talent || '—'}</span>
                      {a.date && <span className="text-muted-foreground ml-2">{formatDate(a.date)}</span>}
                    </Link>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-xs">{a.platform}</td>
                <td className="px-3 py-2 text-xs">{a.action}</td>
                <td className="px-3 py-2">
                  {a.succeeded ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                      ✓ ok
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-destructive" title={a.error_message || ''}>
                      ✗ failed
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground font-mono">
                  {a.performed_by ? a.performed_by.slice(0, 8) : 'system'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatWhen(iso, tz) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  return formatStamp(iso, tz);
}

function formatDate(s) {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
